/**
 * apps/web/src/lib/authPaths.ts
 *
 * Decides which requests may trigger the global 401 -> /login hard redirect.
 *
 * A 401 means "session expired, go sign in" for an ordinary resource, but on
 * the auth endpoints it is an expected answer the calling screen renders
 * itself. `/auth/me` is the important one: AuthProvider probes it on EVERY
 * mount, including on public pages, so its 401 is just "nobody is signed in".
 *
 * Redirecting on it broke magic-link sign-in. The probe leaves before the
 * landing page has POSTed its token; its 401 called
 * `window.location.assign('/login')`, and that full-page navigation ABORTED the
 * in-flight verify POST, so the cookie was never set and the user landed on the
 * login form holding a spent single-use token.
 *
 * Extracted from lib/api.ts so the rule is testable without standing up axios.
 */

/** Requests whose own UI handles a 401, so they must never redirect. */
export const AUTH_PATHS: readonly string[] = [
  '/auth/me',
  '/auth/login',
  '/auth/logout',
  // Covers /auth/magic-link and /auth/magic-link/verify alike.
  '/auth/magic-link',
];

/**
 * Does `path` end at `exempt`, or continue into a sub-path of it?
 * `/auth/magic-link` must cover `/auth/magic-link/verify` but never
 * `/auth/magic-link-decoy`, so the next character has to be a separator.
 */
function coversPath(path: string, exempt: string): boolean {
  const at = path.indexOf(exempt);
  if (at === -1) return false;
  const after = path[at + exempt.length];
  return after === undefined || after === '/';
}

/** Path portion of `url`, absolute or relative. */
function pathOf(url: string): string {
  const withoutQuery = url.split(/[?#]/)[0] ?? url;
  // Only an absolute url has a scheme separator; the path starts at the first
  // slash after it.
  const scheme = withoutQuery.indexOf('//');
  if (scheme === -1) return withoutQuery;
  const slash = withoutQuery.indexOf('/', scheme + 2);
  return slash === -1 ? '/' : withoutQuery.slice(slash);
}

/**
 * True when a 401 on `url` should send the user to /login.
 *
 * Matching is on the PATH, so a query value like `?q=/auth/me` cannot smuggle
 * an exemption. A url we cannot read a path out of is treated as redirectable:
 * failing towards the login screen is the safe direction.
 */
export function shouldRedirectOnUnauthorized(url: string | undefined): boolean {
  if (!url) return true;
  return !AUTH_PATHS.some((exempt) => coversPath(pathOf(url), exempt));
}
